import { Button } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ToggleSwitch } from "../../components/atoms/ToggleSwitch";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { Modal } from "../../components/molecules/Modal/Modal";
import { useAppSelector } from "../../store/hooks";
import { selectSession } from "../../store/selectors/sessionSelector";
import { facilitiesApi } from "../../services/facilities.api";
import { licenceApi } from "../../services/licence.api";
import { promotionApi } from "../../services/promotion.api";
import { formatMoney, formatMoneyOrNull } from "../../utils/money-utils";
import { formatDateTime } from "../../utils/date-format-utils";
import { LicencePlan, LicenceResponse } from "../../types/licence.definition";
import { ActivePromotion } from "../../types/promotion.definition";
import { RoleEnum } from "../../types/user.definition";
import {
  DoctorLicencePlanType,
  FacilityContractDuration,
  LicenceStatus,
} from "../../types/enums";

export type ContractDuration = "1_MONTH" | "1_YEAR" | "3_YEARS" | "5_YEARS";

export const TRIAL_DURATION_OPTIONS = [1, 2, 3, 4] as const;
export type TrialDuration = (typeof TRIAL_DURATION_OPTIONS)[number];

export const CONTRACT_DURATION_MAP: Record<ContractDuration, string> = {
  "1_MONTH": "ONE_MONTH",
  "1_YEAR": FacilityContractDuration.ONE_YEAR,
  "3_YEARS": FacilityContractDuration.THREE_YEARS,
  "5_YEARS": FacilityContractDuration.FIVE_YEARS,
};

export const contractDurationOptions: {
  value: ContractDuration;
  discount: number;
}[] = [
  { value: "1_MONTH", discount: 0 },
  { value: "1_YEAR", discount: 10 },
  { value: "3_YEARS", discount: 20 },
  { value: "5_YEARS", discount: 30 },
];

export function getDiscountRate(duration: ContractDuration | null): number {
  return contractDurationOptions.find(o => o.value === duration)?.discount ?? 0;
}

export function calculateTotalPrice(
  plan: LicencePlan,
  doctorCount: number,
  duration: ContractDuration | null
): number {
  const total = plan.basePrice + doctorCount * (plan.pricePerDoctor ?? 20000);
  const discount = (total * getDiscountRate(duration)) / 100;
  return total - discount;
}

interface Props {
  facilityId: number;
}

/**
 * MED-1583: true when the API reports a promotion active today that changes
 * what the facility is billed. The billed amount comes from the API
 * (discountedMonthlyAmount, priced by the same code that bills invoices); it
 * is never recomputed here.
 */
function hasPromotionalPrice(licence: LicenceResponse): boolean {
  return (
    licence.discountedMonthlyAmount != null &&
    licence.discountedMonthlyAmount !== licence.monthlyAmount
  );
}

export const FacilityLicenceSection: React.FC<Props> = ({ facilityId }) => {
  const { t } = useTranslation();
  const session = useAppSelector(selectSession);
  const isSuperAdmin = session?.user?.roleName === RoleEnum.SUPER_ADMIN;

  const [licence, setLicence] = useState<LicenceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [licenceNotFound, setLicenceNotFound] = useState(false);
  const [activePromotion, setActivePromotion] =
    useState<ActivePromotion | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showUnpauseModal, setShowUnpauseModal] = useState(false);

  // Create form state
  const [plans, setPlans] = useState<LicencePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<LicencePlan | null>(null);
  const [doctorCount, setDoctorCount] = useState(1);
  const [contractDuration, setContractDuration] =
    useState<ContractDuration>("1_YEAR");
  const [isTrial, setIsTrial] = useState(false);
  const [trialDurationMonths, setTrialDurationMonths] =
    useState<TrialDuration>(3);
  const [creating, setCreating] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [unpausing, setUnpausing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // MED-1758: a new licence must cover the facility's active staff.
  const [activeStaff, setActiveStaff] = useState({
    doctors: 0,
    secretaries: 0,
  });
  const minDoctorCount = Math.max(1, activeStaff.doctors);

  useEffect(() => {
    if (selectedPlan && selectedPlan.maxSecretaries < activeStaff.secretaries) {
      setSelectedPlan(null);
    }
  }, [selectedPlan, activeStaff.secretaries]);

  const loadLicence = useCallback(() => {
    setLoading(true);
    setLicenceNotFound(false);
    licenceApi
      .getLicenceByFacility(facilityId)
      .then(data => {
        setLicence(data);
      })
      .catch((err: { code?: string }) => {
        if (err?.code === "LICENCE_NOT_FOUND") {
          setLicenceNotFound(true);
        }
      })
      .finally(() => setLoading(false));
  }, [facilityId]);

  useEffect(() => {
    loadLicence();
  }, [loadLicence]);

  // MED-1583: the licence only carries the billed amount; the promotion's
  // code, rebate and end date come from the facility's active promotions.
  // Best effort — without them the billed amount is still shown.
  const promotional = licence != null && hasPromotionalPrice(licence);
  const discountedMonthlyAmount = licence?.discountedMonthlyAmount;
  useEffect(() => {
    setActivePromotion(null);
    if (!promotional) return;
    let cancelled = false;
    promotionApi
      .getActiveFacilityPromotions(facilityId)
      .then(promotions => {
        if (cancelled) return;
        const match =
          promotions.find(
            p => p.discountedMonthlyAmount === discountedMonthlyAmount
          ) ??
          promotions[0] ??
          null;
        setActivePromotion(match);
      })
      .catch(() => {
        /* the promotion line is optional */
      });
    return () => {
      cancelled = true;
    };
  }, [facilityId, promotional, discountedMonthlyAmount]);

  const openCreateModal = () => {
    setActionError(null);
    setDoctorCount(1);
    setContractDuration("1_YEAR");
    setIsTrial(false);
    setTrialDurationMonths(3);
    setShowCreateModal(true);
    setActiveStaff({ doctors: 0, secretaries: 0 });
    // Best effort: the API refuses a licence that is too small either way.
    facilitiesApi
      .fetchActiveStaffCounts(facilityId)
      .then(counts => {
        setActiveStaff(counts);
        setDoctorCount(Math.max(1, counts.doctors));
      })
      .catch(() => {
        /* the floor is optional */
      });
    setPlansLoading(true);
    licenceApi
      .getAvailablePlans()
      .then(fetched => {
        setPlans(fetched);
        const professional = fetched.find(
          p => p.type === DoctorLicencePlanType.STANDARD
        );
        setSelectedPlan(professional ?? fetched[0] ?? null);
      })
      .finally(() => setPlansLoading(false));
  };

  const handleCreate = async () => {
    if (!selectedPlan) return;
    setCreating(true);
    setActionError(null);
    try {
      const created = await licenceApi.createLicence(facilityId, {
        licenceType: selectedPlan.type,
        numberOfDoctors: doctorCount,
        contractDuration: CONTRACT_DURATION_MAP[contractDuration],
        healthcareFacilityId: facilityId,
        ...(isTrial ? { trialDurationMonths } : {}),
      });
      setLicence(created);
      setLicenceNotFound(false);
      setShowCreateModal(false);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      setActionError(
        code === "DOCTOR_LICENCE_LIMIT_REACHED" ||
          code === "SECRETARY_LICENCE_LIMIT_REACHED"
          ? t(`apiErrors.${code}`)
          : t("facilityLicence.createError")
      );
    } finally {
      setCreating(false);
    }
  };

  const handlePause = async () => {
    if (!licence) return;
    setPausing(true);
    setActionError(null);
    try {
      const updated = await licenceApi.pauseLicence(facilityId, licence.id);
      setLicence(updated);
      setShowPauseModal(false);
    } catch {
      setActionError(t("facilityLicence.pauseError"));
    } finally {
      setPausing(false);
    }
  };

  const handleUnpause = async () => {
    if (!licence) return;
    setUnpausing(true);
    setActionError(null);
    try {
      const updated = await licenceApi.unpauseLicence(facilityId, licence.id);
      setLicence(updated);
      setShowUnpauseModal(false);
    } catch {
      setActionError(t("facilityLicence.unpauseError"));
    } finally {
      setUnpausing(false);
    }
  };

  const statusColor = (status: LicenceStatus) => {
    switch (status) {
      case LicenceStatus.ACTIVE:
        return "text-success-700 bg-success-100";
      case LicenceStatus.EXPIRED:
        return "text-warning-700 bg-warning-100";
      case LicenceStatus.PAUSED:
        return "text-neutral-600 bg-neutral-100";
      case LicenceStatus.TRIAL:
        return "text-primary-700 bg-primary-100";
      default:
        return "text-neutral-600 bg-neutral-100";
    }
  };

  if (loading) {
    return (
      <div className="page-section card-container">
        <div className="h-6 w-32 bg-neutral-200 rounded animate-pulse mb-4" />
        <div className="h-4 w-full bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <>
      <div className="page-section card-container">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("facilityLicence.title")}
          </h3>
          <div className="flex items-center gap-2">
            {licenceNotFound && (
              <Button
                onClick={openCreateModal}
                className="flex items-center gap-2"
              >
                <span>+</span>
                <span>{t("facilityLicence.createLicence")}</span>
              </Button>
            )}
            {licence &&
              isSuperAdmin &&
              licence.status !== LicenceStatus.PAUSED && (
                <Button
                  variant="outline"
                  onClick={() => setShowPauseModal(true)}
                  className="text-warning-700 border-warning-300 hover:bg-warning-50"
                >
                  {t("facilityLicence.pauseLicence")}
                </Button>
              )}
            {licence &&
              isSuperAdmin &&
              licence.status === LicenceStatus.PAUSED && (
                <Button
                  variant="outline"
                  onClick={() => setShowUnpauseModal(true)}
                  className="text-success-700 border-success-300 hover:bg-success-50"
                >
                  {t("facilityLicence.unpauseLicence")}
                </Button>
              )}
          </div>
        </div>

        {licenceNotFound ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mb-3">
              <svg
                className="w-6 h-6 text-neutral-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-sm font-medium text-neutral-700">
              {t("facilityLicence.noLicence")}
            </p>
            <p className="text-xs text-neutral-500 mt-1">
              {t("facilityLicence.noLicenceDescription")}
            </p>
          </div>
        ) : licence ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <div>
              <p className="text-xs text-neutral-500">
                {t("facilityLicence.plan")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 mt-0.5">
                {licence.licenceType}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">
                {t("facilityLicence.status")}
              </p>
              <span
                className={`inline-block mt-0.5 text-xs font-medium px-2 py-0.5 rounded-full ${statusColor(licence.status)}`}
              >
                {t(`facilityLicence.statuses.${licence.status}`, {
                  defaultValue: licence.status,
                })}
              </span>
            </div>
            <div>
              <p className="text-xs text-neutral-500">
                {t("facilityLicence.doctors")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 mt-0.5">
                {licence.numberOfDoctors}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">
                {t("facilityLicence.endDate")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 mt-0.5">
                {/* Java LocalDate arrives as [YYYY, M, D] or "2026-08-05"; the
                    shared formatter handles both, parses a date-only string
                    at LOCAL midnight rather than through UTC so the day never
                    slides, and renders in the UI's language (MED-1475). */}
                {formatDateTime(licence.endDate as string | number[] | null)}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">
                {promotional
                  ? t("facilityLicence.contractualPrice")
                  : t("facilityLicence.monthlyAmount")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 mt-0.5">
                {formatMoneyOrNull(licence.monthlyAmount) ?? "—"}
              </p>
            </div>
          </div>
        ) : null}

        {licence && promotional && (
          <div
            className="mt-4 flex flex-wrap items-end justify-between gap-4 border-t border-neutral-200 pt-4"
            data-testid="licence-promotion"
          >
            {activePromotion && (
              <p className="text-sm font-medium text-success-700">
                {[
                  t("facilityLicence.promotion", {
                    code: activePromotion.promotionCode ?? "",
                  }).trim(),
                  activePromotion.rebate != null
                    ? t("facilityLicence.promotionRebate", {
                        rebate: activePromotion.rebate,
                      })
                    : null,
                  activePromotion.endDate
                    ? t("facilityLicence.promotionUntil", {
                        date: formatDateTime(activePromotion.endDate),
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}
            <div>
              <p className="text-xs text-neutral-500">
                {t("facilityLicence.billedAmount")}
              </p>
              <p className="text-sm font-semibold text-neutral-900 mt-0.5">
                {licence.discountedMonthlyAmount === 0
                  ? t("facilityLicence.offered")
                  : formatMoney(licence.discountedMonthlyAmount as number)}
              </p>
            </div>
          </div>
        )}

        {actionError && (
          <p className="mt-3 text-sm text-danger-600">{actionError}</p>
        )}
      </div>

      {/* Create Licence Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={t("facilityLicence.createLicence")}
      >
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Doctor Count */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">
              {t("onboarding.steps.doctorCount.title")}
            </h4>
            <div className="flex items-center gap-3 mt-3">
              <button
                className="flex justify-center items-center w-8 h-8 text-lg font-bold rounded-lg border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40"
                onClick={() =>
                  setDoctorCount(c => Math.max(minDoctorCount, c - 1))
                }
                disabled={doctorCount <= minDoctorCount}
              >
                −
              </button>
              <input
                type="number"
                min={minDoctorCount}
                value={doctorCount}
                onChange={e => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= minDoctorCount) setDoctorCount(val);
                }}
                className="w-20 text-center text-lg font-semibold rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                className="flex justify-center items-center w-8 h-8 text-lg font-bold rounded-lg border border-gray-300 bg-white hover:bg-gray-100"
                onClick={() => setDoctorCount(c => c + 1)}
              >
                +
              </button>
              <span className="text-sm text-gray-500">
                {/* MED-1543: a frozen plural read "1 médecins". */}
                {t("onboarding.steps.doctorCount.doctors", {
                  count: doctorCount,
                })}
              </span>
            </div>
            {activeStaff.doctors > 1 && (
              <p className="mt-2 text-xs text-gray-500">
                {t("facilityLicence.doctorFloor", {
                  count: activeStaff.doctors,
                })}
              </p>
            )}
          </div>

          {/* Free Trial Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-900">
              {t("facilityLicence.startTrialLabel")}
            </h4>
            <ToggleSwitch
              checked={isTrial}
              onChange={setIsTrial}
              ariaLabel={t("facilityLicence.startTrialLabel")}
            />
          </div>

          {/* Trial Duration */}
          {isTrial && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {t("onboarding.steps.trialDuration.title")}
              </h4>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 mt-3">
                {TRIAL_DURATION_OPTIONS.map(option => {
                  const isSelected = trialDurationMonths === option;
                  return (
                    <button
                      key={option}
                      className={`relative overflow-hidden p-3 rounded-lg border text-center transition-all ${
                        isSelected
                          ? "border-blue-500 bg-blue-50 text-blue-700 shadow-md"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                      onClick={() => setTrialDurationMonths(option)}
                    >
                      <div className="font-semibold text-sm">
                        {t(
                          `onboarding.steps.trialDuration.${option}_MONTH${option > 1 ? "S" : ""}`
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Contract Duration */}
          {!isTrial && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {t("onboarding.steps.contractDuration.title")}
              </h4>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 mt-3">
                {contractDurationOptions.map(option => {
                  const isSelected = contractDuration === option.value;
                  return (
                    <button
                      key={option.value}
                      className={`relative overflow-hidden p-3 rounded-lg border text-center transition-all ${
                        isSelected
                          ? "border-blue-500 bg-blue-50 text-blue-700 shadow-md"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                      onClick={() => setContractDuration(option.value)}
                    >
                      <div className="font-semibold text-sm">
                        {t(`onboarding.steps.contractDuration.${option.value}`)}
                      </div>
                      {option.discount > 0 ? (
                        <div className="mt-0.5 text-xs font-medium text-green-600">
                          -{option.discount}%{" "}
                          {t("onboarding.steps.contractDuration.discount")}
                        </div>
                      ) : (
                        <div className="mt-0.5 text-xs text-gray-400">
                          {t("onboarding.steps.contractDuration.noDiscount")}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Plan Selection */}
          {plansLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {plans.map(plan => {
                const isSelected = selectedPlan?.type === plan.type;
                const hasTooFewSecretaries =
                  plan.maxSecretaries < activeStaff.secretaries;
                const isRecommended =
                  plan.type === DoctorLicencePlanType.STANDARD;
                const totalPrice = calculateTotalPrice(
                  plan,
                  doctorCount,
                  contractDuration
                );
                const rawTotal =
                  plan.basePrice + doctorCount * (plan.pricePerDoctor ?? 20000);
                const discountRate = getDiscountRate(contractDuration);
                const savings = rawTotal - totalPrice;
                const planKey = plan.type
                  .toLowerCase()
                  .replace("standard", "professional");

                return (
                  <div
                    key={plan.type}
                    aria-disabled={hasTooFewSecretaries}
                    className={`relative overflow-hidden border rounded-xl p-5 transition-all ${
                      hasTooFewSecretaries
                        ? "border-gray-200 opacity-60 cursor-not-allowed"
                        : isSelected
                          ? "border-blue-500 bg-blue-50 shadow-lg cursor-pointer"
                          : isRecommended
                            ? "border-blue-300 hover:border-blue-400 shadow-md cursor-pointer"
                            : "border-gray-200 hover:border-gray-300 cursor-pointer"
                    }`}
                    onClick={() =>
                      !hasTooFewSecretaries && setSelectedPlan(plan)
                    }
                  >
                    {isRecommended && (
                      <div className="absolute top-5 right-[-36px] w-40 bg-blue-500 text-white text-xs font-bold text-center py-1 rotate-45 shadow">
                        {t("licenses.recommended")}
                      </div>
                    )}
                    <h4
                      className={`text-base font-semibold ${isRecommended ? "text-blue-700" : "text-gray-900"}`}
                    >
                      {t(`licenses.plans.${planKey}`)}
                    </h4>
                    <p className="mt-1 text-xs text-gray-600">
                      {t(`licenses.plans.${planKey}Description`)}
                    </p>
                    {hasTooFewSecretaries && (
                      <p className="mt-1 text-xs font-medium text-amber-700">
                        {t("facilityLicence.planTooFewSecretaries", {
                          count: activeStaff.secretaries,
                        })}
                      </p>
                    )}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-lg font-bold text-gray-900">
                        {formatMoney(totalPrice)}
                        <span className="text-xs font-normal text-gray-500">
                          /{t("licenses.month")}
                        </span>
                      </p>
                      {discountRate > 0 && savings > 0 && (
                        <p className="text-xs text-green-600 font-medium mt-0.5">
                          -{discountRate}% · {t("licenses.save")}{" "}
                          {formatMoney(savings)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {actionError && (
            <p className="text-sm text-danger-600">{actionError}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleCreate} disabled={creating || !selectedPlan}>
              {creating
                ? t("common.saving")
                : t("facilityLicence.confirmCreate")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Pause Confirmation Modal */}
      <ConfirmationModal
        isOpen={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        onConfirm={handlePause}
        title={t("facilityLicence.pauseConfirmTitle")}
        message={t("facilityLicence.pauseConfirmMessage")}
        confirmLabel={
          pausing ? t("common.saving") : t("facilityLicence.pauseLicence")
        }
        cancelLabel={t("common.cancel")}
        variant="warning"
      />

      {/* Unpause Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUnpauseModal}
        onClose={() => setShowUnpauseModal(false)}
        onConfirm={handleUnpause}
        title={t("facilityLicence.unpauseConfirmTitle")}
        message={t("facilityLicence.unpauseConfirmMessage")}
        confirmLabel={
          unpausing ? t("common.saving") : t("facilityLicence.unpauseLicence")
        }
        cancelLabel={t("common.cancel")}
        variant="warning"
      />
    </>
  );
};
